/**
 * Processamento de imagens para SEO: redimensionar/cortar para 9:16 (vertical) ou 16:9 (horizontal).
 * Mantém a resolução e o foco da imagem principal (center crop).
 * Site: www.naviexperience.com
 */

const SITE_NAME = 'NaviExperience';

export type AspectOrientation = 'vertical' | 'horizontal';

const ASPECT_VERTICAL = 9 / 16;
const ASPECT_HORIZONTAL = 16 / 9;

/** Detecta orientação da imagem: width > height = horizontal. */
function getNaturalOrientation(width: number, height: number): AspectOrientation {
  return width >= height ? 'horizontal' : 'vertical';
}

/**
 * Faz crop central para o aspect ratio desejado.
 * Mantém a maior resolução possível sem distorcer.
 */
function cropToAspect(
  img: HTMLImageElement,
  targetAspect: number,
  maxSize = 1920
): { canvas: HTMLCanvasElement; width: number; height: number } {
  const w = img.naturalWidth;
  const h = img.naturalHeight;
  const srcAspect = w / h;

  let cropW: number;
  let cropH: number;

  if (srcAspect > targetAspect) {
    cropH = h;
    cropW = h * targetAspect;
  } else {
    cropW = w;
    cropH = w / targetAspect;
  }

  const scale = Math.min(1, maxSize / cropW, maxSize / cropH);
  const outW = Math.round(cropW * scale);
  const outH = Math.round(cropH * scale);

  const sx = (w - cropW) / 2;
  const sy = (h - cropH) / 2;

  const canvas = document.createElement('canvas');
  canvas.width = outW;
  canvas.height = outH;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Canvas 2d não disponível');

  ctx.drawImage(img, sx, sy, cropW, cropH, 0, 0, outW, outH);
  return { canvas, width: outW, height: outH };
}

/**
 * Detecta orientação da imagem carregada e processa para o aspect correspondente.
 */
export function processImageAutoAspect(imageUrl: string, quality = 0.9): Promise<string> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      try {
        const orient = getNaturalOrientation(img.naturalWidth, img.naturalHeight);
        const targetAspect = orient === 'vertical' ? ASPECT_VERTICAL : ASPECT_HORIZONTAL;
        const { canvas } = cropToAspect(img, targetAspect);
        resolve(canvas.toDataURL('image/jpeg', quality));
      } catch (e) {
        reject(e instanceof Error ? e : new Error(String(e)));
      }
    };
    img.onerror = () => reject(new Error('Falha ao carregar imagem'));
    img.src = imageUrl;
  });
}

/**
 * Processa uma URL de imagem e retorna data URL no formato 9:16 ou 16:9.
 * Se a imagem já estiver no formato, redimensiona para melhor resolução.
 */
export function processImageToAspect(
  imageUrl: string,
  orientation: AspectOrientation,
  quality = 0.92
): Promise<string> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';

    img.onload = () => {
      try {
        const targetAspect = orientation === 'vertical' ? ASPECT_VERTICAL : ASPECT_HORIZONTAL;
        const { canvas } = cropToAspect(img, targetAspect);
        const dataUrl = canvas.toDataURL('image/jpeg', quality);
        resolve(dataUrl);
      } catch (e) {
        reject(e instanceof Error ? e : new Error(String(e)));
      }
    };

    img.onerror = () => reject(new Error('Falha ao carregar imagem'));

    img.src = imageUrl;
  });
}

/**
 * Processa uma imagem e gera AMBAS as versões (vertical e horizontal).
 */
export function processImageToBothAspects(
  imageUrl: string,
  quality = 0.9
): Promise<{ vertical: string; horizontal: string }> {
  return Promise.all([
    processImageToAspect(imageUrl, 'vertical', quality),
    processImageToAspect(imageUrl, 'horizontal', quality),
  ]).then(([vertical, horizontal]) => ({ vertical, horizontal }));
}

/**
 * Comprime foto para persistência no localStorage (cadastro de cidade, etc.).
 * Escala o maior lado para no máximo `maxSide` px e exporta JPEG.
 */
export function compressImageFileForStorage(
  file: File,
  options?: { maxSide?: number; quality?: number }
): Promise<string> {
  const maxSide = options?.maxSide ?? 1280;
  const quality = options?.quality ?? 0.82;
  if (!file.type.startsWith('image/')) {
    return Promise.reject(new Error('Selecione um arquivo de imagem.'));
  }
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const src = reader.result as string;
      if (!src) {
        reject(new Error('Não foi possível ler o arquivo.'));
        return;
      }
      const img = new Image();
      img.onload = () => {
        try {
          const w = img.naturalWidth;
          const h = img.naturalHeight;
          if (!w || !h) {
            reject(new Error('Imagem inválida ou vazia.'));
            return;
          }
          const scale = Math.min(1, maxSide / Math.max(w, h));
          const outW = Math.max(1, Math.round(w * scale));
          const outH = Math.max(1, Math.round(h * scale));
          const canvas = document.createElement('canvas');
          canvas.width = outW;
          canvas.height = outH;
          const ctx = canvas.getContext('2d');
          if (!ctx) {
            reject(new Error('Canvas 2d não disponível.'));
            return;
          }
          ctx.drawImage(img, 0, 0, outW, outH);
          resolve(canvas.toDataURL('image/jpeg', quality));
        } catch (e) {
          reject(e instanceof Error ? e : new Error(String(e)));
        }
      };
      img.onerror = () => reject(new Error('Falha ao carregar a imagem. Tente JPG ou PNG.'));
      img.src = src;
    };
    reader.onerror = () => reject(new Error('Falha ao ler o arquivo.'));
    reader.readAsDataURL(file);
  });
}

/** Tamanho aproximado em bytes do payload base64 de um data URL. */
function dataUrlPayloadBytes(dataUrl: string): number {
  const i = dataUrl.indexOf(',');
  const base64 = i >= 0 ? dataUrl.slice(i + 1) : dataUrl;
  return Math.floor((base64.length * 3) / 4);
}

/**
 * Redimensiona e comprime uma imagem até o data URL resultante ficar abaixo de `maxBytes`
 * (útil para limite de 5MB no upload manual / localStorage).
 */
export function compressImageFileToMaxBytes(
  file: File,
  maxBytes: number,
  options?: { minMaxSide?: number; minQuality?: number }
): Promise<string> {
  const minMaxSide = options?.minMaxSide ?? 360;
  const minQuality = options?.minQuality ?? 0.4;
  if (!file.type.startsWith('image/')) {
    return Promise.reject(new Error('Selecione um arquivo de imagem.'));
  }

  let maxSide = 2200;
  let quality = 0.85;

  const attempt = () => compressImageFileForStorage(file, { maxSide, quality });

  return (async () => {
    for (let i = 0; i < 18; i++) {
      const dataUrl = await attempt();
      if (dataUrlPayloadBytes(dataUrl) <= maxBytes) return dataUrl;
      maxSide = Math.max(minMaxSide, Math.round(maxSide * 0.82));
      quality = Math.max(minQuality, quality - 0.055);
    }
    const last = await attempt();
    if (dataUrlPayloadBytes(last) > maxBytes) {
      throw new Error(
        'Não foi possível reduzir a imagem o suficiente. Tente um arquivo menor ou outra foto.'
      );
    }
    return last;
  })();
}

/**
 * Gera alt e title para SEO.
 */
export function seoAltTitle(itemName: string, index: number): { alt: string; title: string } {
  const base = `${SITE_NAME} - ${itemName} - Foto ${index + 1}`;
  return { alt: base, title: base };
}
